import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { Check, ChevronDown, ChevronRight, Clock, X } from 'lucide-react';
import { Fragment, useState } from 'react';

import type {
  EnrollmentSupport,
  PayerEnrollmentGroup,
  UnlinkedInsurance,
} from '../../services/clinicsApi';
import { RETIRED_PAYER_LABEL } from '../../utils/insuranceOptions';
import { PayerName } from './PayerName';
import { SupportIcon } from './SupportIcon';
import {
  PAYER_TRANSACTION_LABELS,
  PAYER_STATUS_LABELS,
  agencyTitle,
  clinicLabel,
  markLabel,
  linesInView,
  payerStatus,
  transactionStates,
  type PayerStatus as PayerStatusValue,
  type PayerTransaction,
  type SummaryTone,
} from './payerEnrollments';

type TableInsurance = UnlinkedInsurance & { retired_in_wellsky?: boolean };

interface PayerEnrollmentsTableProps {
  payers: PayerEnrollmentGroup[];
  noPayerInsurances: TableInsurance[];
  /** Payers whose clinic lines in view are all retired in WellSky. */
  retiredPayers?: PayerEnrollmentGroup[];
  /** Insurances no payer pays that are retired in WellSky. */
  retiredInsurances?: TableInsurance[];
  clinicIds: string[];
  /** The transaction columns to show, in order. */
  transactions: PayerTransaction[];
  /** The payer shown beside the table. */
  selectedKey?: string | null;
  /** Show this payer beside the table. */
  onSelect?: (payerKey: string) => void;
  /**
   * Given, a payer row opens its clinic lines and a clinic line is what gets
   * picked: that clinic's insurance with the payer, shown beside the table.
   */
  onSelectLine?: (payerKey: string, clinicId: string) => void;
  selectedLine?: { payerKey: string; clinicId: string } | null;
  /** Picks an insurance from the Not in Roger's payer list group. */
  onSelectInsurance?: (insuranceId: string) => void;
  selectedInsuranceId?: string | null;
}

// Bold so the status reads as the row's answer, in the ticks' colours: red to
// act on, green done, grey not live yet.
const STATUS_CLASSES: Record<PayerStatusValue, string> = {
  rejected: 'font-medium text-red-600',
  needs_action: 'font-medium text-red-600',
  waiting: 'font-medium text-muted-foreground',
  not_started: 'font-medium text-muted-foreground',
  enrolled: 'font-medium text-green-700',
  none_needed: 'font-medium text-muted-foreground',
};

/** The payer's status after its ID. */
function PayerStatus({
  payer,
  clinicIds,
  transactions,
}: {
  payer: PayerEnrollmentGroup;
  clinicIds: string[];
  transactions: PayerTransaction[];
}) {
  const status = payerStatus(payer, clinicIds, transactions);
  if (!status) return null;
  return (
    <>
      {' · '}
      <span className={STATUS_CLASSES[status]}>
        {PAYER_STATUS_LABELS[status]}
      </span>
    </>
  );
}

/**
 * A clinic line's mark: a green check when this clinic is done (live, or no
 * enrollment needed), a grey clock once submitted, a red cross while the agency
 * has something to do, nothing when the payer does not
 * support the transaction. The payer row carries the detail; this is the
 * at-a-glance answer per clinic.
 */
function ClinicDot({
  support,
  state,
}: {
  support?: EnrollmentSupport;
  state: SummaryTone | null;
}) {
  if (
    support === 'not_supported' ||
    (support !== 'enrollment_required' && !state)
  ) {
    // Still says why, on hover, like every other mark.
    const why = markLabel(support, state);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span role='img' aria-label={why} className='text-muted-foreground'>
            —
          </span>
        </TooltipTrigger>
        <TooltipContent>{why}</TooltipContent>
      </Tooltip>
    );
  }
  const done =
    support === 'enrollment_not_required' ||
    state === 'enrolled' ||
    state === 'none_needed';
  const label = markLabel(support, state);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role='img'
          aria-label={label}
          className='inline-flex items-center justify-center'
        >
          {done ? (
            <Check className='h-3.5 w-3.5 text-green-600' strokeWidth={4} />
          ) : state === 'waiting' ? (
            <Clock
              className='h-3.5 w-3.5 text-muted-foreground'
              strokeWidth={3}
            />
          ) : (
            <X className='h-3.5 w-3.5 text-red-600' strokeWidth={4} />
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * One row per payer: what the payer supports per transaction, with the tick
 * coloured by where the clinics in view stand. A row opens to one line per
 * clinic with that clinic's own ticks; a click shows the payer and its steps
 * beside the table.
 */
export function PayerEnrollmentsTable({
  payers,
  noPayerInsurances,
  retiredPayers = [],
  retiredInsurances = [],
  clinicIds,
  transactions,
  selectedKey,
  onSelect,
  onSelectLine,
  selectedLine,
  onSelectInsurance,
  selectedInsuranceId,
}: PayerEnrollmentsTableProps) {
  const [showNoPayer, setShowNoPayer] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setOpen(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderPayer = (payer: PayerEnrollmentGroup) => {
    const title = agencyTitle(payer, clinicIds);
    const states = new Map(
      transactionStates(payer, clinicIds, transactions).map(item => [
        item.transaction,
        item.state,
      ])
    );
    const isOpen = open.has(payer.key);
    return (
      <Fragment key={payer.key}>
        <TableRow
          onClick={() => onSelect?.(payer.key)}
          aria-selected={selectedKey === payer.key}
          className={`cursor-pointer ${
            selectedKey === payer.key ? 'bg-muted hover:bg-muted' : ''
          }`}
        >
          <TableCell>
            {/* A click anywhere shows the payer beside the table;
                  the chevron alone opens its clinic lines. */}
            <div className='flex items-center gap-2'>
              <button
                type='button'
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Hide clinics' : 'Show clinics'}
                onClick={event => {
                  event.stopPropagation();
                  toggle(payer.key);
                }}
                className='shrink-0 rounded text-muted-foreground hover:text-foreground'
              >
                {isOpen ? (
                  <ChevronDown className='h-5 w-5' />
                ) : (
                  <ChevronRight className='h-5 w-5' />
                )}
              </button>
              <div className='min-w-0 flex-1'>
                <div className='flex items-center gap-1.5'>
                  {/* One line: a long name ends in "…", and the count
                      of other names stays whole after it. */}
                  <button
                    type='button'
                    onClick={event => {
                      event.stopPropagation();
                      onSelect?.(payer.key);
                    }}
                    className='flex max-w-full text-left font-medium'
                    title={
                      title
                        ? `${title.all.join(', ')} · paid by ${payer.name}`
                        : payer.name
                    }
                  >
                    <PayerName title={title} fallback={payer.name} />
                  </button>
                </div>
                <div className='text-xs text-muted-foreground'>
                  {payer.primary_payer_id ?? 'No payer ID'}
                  <PayerStatus
                    payer={payer}
                    clinicIds={clinicIds}
                    transactions={transactions}
                  />
                </div>
              </div>
            </div>
          </TableCell>
          {transactions.map(transaction => (
            <TableCell key={transaction} className='text-center'>
              <SupportIcon
                support={payer[transaction]?.support}
                state={states.get(transaction) ?? null}
                size='medium'
              />
            </TableCell>
          ))}
        </TableRow>
        {isOpen &&
          linesInView(payer, clinicIds).map(line => {
            const lineStates = new Map(
              transactionStates(
                payer,
                [line.agency_clinic_id],
                transactions
              ).map(item => [item.transaction, item.state])
            );
            return (
              <TableRow
                key={`${payer.key}-${line.agency_clinic_id}`}
                onClick={event => {
                  // Not the payer row's own click as well.
                  event.stopPropagation();
                  onSelectLine?.(payer.key, line.agency_clinic_id);
                }}
                aria-selected={
                  selectedLine?.payerKey === payer.key &&
                  selectedLine.clinicId === line.agency_clinic_id
                }
                className={
                  selectedLine?.payerKey === payer.key &&
                  selectedLine.clinicId === line.agency_clinic_id
                    ? 'cursor-pointer bg-muted hover:bg-muted'
                    : onSelectLine
                      ? 'cursor-pointer bg-muted/30 hover:bg-muted/60'
                      : 'bg-muted/30 hover:bg-muted/30'
                }
              >
                <TableCell className='pl-12 text-xs'>
                  {clinicLabel(line.clinic_name)}
                </TableCell>
                {transactions.map(transaction => (
                  <TableCell key={transaction} className='text-center'>
                    <ClinicDot
                      support={payer[transaction]?.support}
                      state={lineStates.get(transaction) ?? null}
                    />
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
      </Fragment>
    );
  };

  const renderInsurance = (insurance: TableInsurance) => (
    <TableRow
      key={insurance.id}
      onClick={() => onSelectInsurance?.(insurance.id)}
      className={
        selectedInsuranceId === insurance.id
          ? 'cursor-pointer bg-muted hover:bg-muted'
          : onSelectInsurance
            ? 'cursor-pointer'
            : 'hover:bg-transparent'
      }
    >
      <TableCell colSpan={transactions.length + 1} className='pl-10 text-sm'>
        {insurance.name}
        {insurance.retired_in_wellsky && (
          <Badge
            variant='outline'
            className='ml-1.5 whitespace-nowrap font-normal'
          >
            {RETIRED_PAYER_LABEL}
          </Badge>
        )}
        <span className='text-muted-foreground'>
          {insurance.clinic_name && ` · ${insurance.clinic_name}`}
          {' · '}
          {insurance.edi_payer_id
            ? `Payer ID ${insurance.edi_payer_id} not recognized`
            : 'No payer ID'}
        </span>
      </TableCell>
    </TableRow>
  );

  if (
    payers.length === 0 &&
    noPayerInsurances.length === 0 &&
    retiredPayers.length === 0 &&
    retiredInsurances.length === 0
  ) {
    return (
      <div className='rounded-md border py-8 text-center text-sm text-muted-foreground'>
        No payers match.
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className='overflow-auto rounded-md border lg:max-h-full'>
        <Table
          className='min-w-[440px] table-fixed'
          containerClassName='overflow-visible'
        >
          <TableHeader className='sticky top-0 z-10 bg-background [&_tr]:border-b-0 [&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]'>
            <TableRow>
              <TableHead>Payer</TableHead>
              {transactions.map(transaction => (
                <TableHead
                  key={transaction}
                  className='w-[5.5rem] px-1 text-center text-xs leading-tight'
                >
                  {transaction === 'claims'
                    ? 'Claims (837I)'
                    : PAYER_TRANSACTION_LABELS[transaction]}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {payers.map(renderPayer)}
            {noPayerInsurances.length > 0 && (
              <>
                <GroupToggleRow
                  open={showNoPayer}
                  onToggle={() => setShowNoPayer(value => !value)}
                  colSpan={transactions.length + 1}
                  label={`Not in Roger's payer list (${noPayerInsurances.length})`}
                />
                {showNoPayer && noPayerInsurances.map(renderInsurance)}
              </>
            )}
            {(retiredPayers.length > 0 || retiredInsurances.length > 0) && (
              <>
                <GroupToggleRow
                  open={showRetired}
                  onToggle={() => setShowRetired(value => !value)}
                  colSpan={transactions.length + 1}
                  label={`Retired (${retiredPayers.length + retiredInsurances.length})`}
                />
                {showRetired && retiredPayers.map(renderPayer)}
                {showRetired && retiredInsurances.map(renderInsurance)}
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}

function GroupToggleRow({
  open,
  onToggle,
  colSpan,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  colSpan: number;
  label: string;
}) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <button
          type='button'
          onClick={onToggle}
          aria-expanded={open}
          className='flex items-center gap-2 text-muted-foreground'
        >
          {open ? (
            <ChevronDown className='h-4 w-4' />
          ) : (
            <ChevronRight className='h-4 w-4' />
          )}
          {label}
        </button>
      </TableCell>
    </TableRow>
  );
}
